import React, { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { MediaTile } from "@/components/MediaTile";
import { MediaLightbox } from "@/components/MediaLightbox";
import { CollectionsRow } from "@/components/CollectionsRow";
import { api, fileUrl, thumbUrl, videoUrl, posterUrl } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Trophy, Star, MapPin, Loader2, ChevronLeft, Image as ImageIcon } from "lucide-react";

const RANK_STYLES = [
  "from-amber-300 to-yellow-500 text-black",
  "from-slate-200 to-slate-400 text-black",
  "from-amber-600 to-amber-800 text-white",
];

const LocationCard = ({ item, rank, onOpen }) => (
  <button data-testid={`leaderboard-card-${item.key}`} onClick={() => onOpen(item)}
    className="group w-full text-left flex items-center gap-4 rounded-xl border border-border bg-card/60 hover:border-primary/50 hover:bg-card transition-all p-3">
    <div className={cn("shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-heading font-black text-lg",
      rank <= 3 ? `bg-gradient-to-br ${RANK_STYLES[rank - 1]}` : "bg-secondary text-muted-foreground")}>
      {rank}
    </div>
    <div className="flex -space-x-3 shrink-0">
      {(item.thumbs || []).slice(0, 3).map((t, i) => (
        <div key={t.id} className={cn("w-12 sm:w-14 h-12 sm:h-14 rounded-lg overflow-hidden border-2 border-card bg-black", i === 2 && "hidden sm:block")} style={{ zIndex: 3 - i }}>
          {t.media_type === "video"
            ? <video src={videoUrl(t)} poster={posterUrl(t, 400)} muted className="w-full h-full object-cover" />
            : <img src={thumbUrl(t.file_ref, 400)} alt="" className="w-full h-full object-cover" />}
        </div>
      ))}
    </div>
    <div className="min-w-0 flex-1">
      <div className="font-heading text-base sm:text-lg truncate">{item.name}</div>
      <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5 flex-wrap">
        {item.country && item.name !== item.country && <span className="truncate max-w-[8rem]">{item.country}</span>}
        <span className="inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {item.count} posts</span>
      </div>
    </div>
    <div className="shrink-0 text-right">
      {item.avg_rating > 0 ? (
        <div className="inline-flex items-center gap-1 font-heading text-xl"><Star className="w-4 h-4 text-primary fill-primary" /> {item.avg_rating}</div>
      ) : (
        <span className="text-xs uppercase tracking-widest text-muted-foreground">New</span>
      )}
    </div>
  </button>
);

const Leaderboards = () => {
  const [level, setLevel] = useState("city");
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null); // location object
  const [media, setMedia] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      if (level === "galleries") {
        const r = await api.get("/popular-galleries");
        setRows(r.data);
      } else {
        const r = await api.get("/leaderboards/locations", { params: { level, limit: 50 } });
        setRows(r.data);
      }
    } catch { setRows([]); }
  }, [level]);
  useEffect(() => { load(); }, [load]);

  const openLocation = async (loc) => {
    setSelected(loc); setMedia(null);
    try {
      const r = await api.get("/leaderboards/location-media", { params: { level, key: loc.key, limit: 60 } });
      setMedia(r.data);
    } catch { setMedia([]); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {!selected ? (
          <>
            <div className="flex items-center gap-3 mb-1">
              <Trophy className="w-7 h-7 text-primary" />
              <h1 className="font-heading text-3xl sm:text-4xl">Top Places</h1>
            </div>
            <p className="text-muted-foreground mb-6 text-sm">The highest-rated feet by location. See the best of every city and country.</p>

            <div className="inline-flex rounded-full border border-border p-1 bg-card mb-6" data-testid="leaderboard-level-toggle">
              {[["city", "Cities"], ["country", "Countries"], ["galleries", "Galleries"]].map(([v, l]) => (
                <button key={v} data-testid={`leaderboard-level-${v}`} onClick={() => setLevel(v)}
                  className={cn("px-5 py-1.5 rounded-full text-sm font-medium transition-colors",
                    level === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
                  {l}
                </button>
              ))}
            </div>

            {!rows ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
              : level === "galleries" ? (
                  rows.length === 0 ? <p className="text-muted-foreground py-20 text-center">No public galleries yet — create one and share it!</p>
                    : <div data-testid="leaderboard-galleries"><CollectionsRow collections={rows} editable={false} /></div>
                )
              : rows.length === 0 ? <p className="text-muted-foreground py-20 text-center">No ranked locations yet.</p>
              : <div className="space-y-2.5" data-testid="leaderboard-list">
                  {rows.map((it, i) => <LocationCard key={it.key} item={it} rank={i + 1} onOpen={openLocation} />)}
                </div>}
          </>
        ) : (
          <>
            <button data-testid="leaderboard-back" onClick={() => { setSelected(null); setMedia(null); }}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
              <ChevronLeft className="w-4 h-4" /> Back to rankings
            </button>
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-6 h-6 text-primary" />
              <h1 className="font-heading text-3xl">Best of {selected.name}</h1>
            </div>
            <p className="text-muted-foreground mb-6 text-sm">
              Top-rated {level === "country" ? "across" : "in"} {selected.name}
              {selected.country && selected.name !== selected.country ? `, ${selected.country}` : ""}.
            </p>
            {!media ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
              : media.length === 0 ? <p className="text-muted-foreground py-20 text-center">No public posts here yet.</p>
              : <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="leaderboard-location-grid">
                  {media.map((item, i) => <MediaTile key={item.id} item={item} index={i} onOpen={(x) => setOpenId(x.id)} />)}
                </div>}
          </>
        )}
      </div>
      <MediaLightbox mediaId={openId} open={!!openId} onClose={() => setOpenId(null)} onNavigate={setOpenId} playlist={media || []} />
    </div>
  );
};

export default Leaderboards;
